import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import apiClient, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { COMPANY_TYPES } from "@/lib/status";
import { Buildings, MapPin, UsersThree, ArrowRight } from "@phosphor-icons/react";

const inputCls = "mt-1.5 w-full h-11 px-3.5 rounded-md border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none transition";
const Field = ({ label, children }) => (
  <div><label className="font-mono-plex text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</label>{children}</div>
);

export default function OnboardingPage() {
  const { company, refreshCompany } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    company_name: company?.company_name || "", contact_name: company?.contact_name || "", phone: "", website: "",
    company_type: "Residential AV / Automation", years_in_business: "", num_technicians: "", num_programmers: "",
    num_engineers: "", num_project_managers: "", num_salespeople: "", num_employees: "",
    country: "USA", state: "", city: "", zip: "", primary_service_area: "", additional_markets: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const nums = ["years_in_business", "num_technicians", "num_programmers", "num_engineers", "num_project_managers", "num_salespeople", "num_employees"];
      const payload = { ...f };
      nums.forEach((k) => (payload[k] = f[k] === "" ? null : parseInt(f[k], 10)));
      payload.additional_markets = f.additional_markets.split(",").map((s) => s.trim()).filter(Boolean);
      await apiClient.put("/company", payload);
      await refreshCompany();
      toast.success("Company profile saved");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] grain py-10 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="font-display font-black text-4xl tracking-tighter text-[#0F172A]">Set up your company</h1>
          <p className="text-slate-500 mt-2">Proposal Forensics uses this to tailor labor rates, market benchmarks and analysis to your business. You can change it anytime.</p>
        </div>
        <form onSubmit={submit} className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-5"><Buildings size={18} weight="bold" /><h2 className="font-display font-bold text-lg text-[#0F172A]">Company</h2></div>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Company Name"><input data-testid="onb-company-name" className={inputCls} value={f.company_name} onChange={set("company_name")} required /></Field>
              <Field label="Contact Name"><input data-testid="onb-contact-name" className={inputCls} value={f.contact_name} onChange={set("contact_name")} /></Field>
              <Field label="Phone"><input className={inputCls} value={f.phone} onChange={set("phone")} /></Field>
              <Field label="Website"><input className={inputCls} value={f.website} onChange={set("website")} placeholder="https://" /></Field>
              <Field label="Company Type"><select data-testid="onb-company-type" className={inputCls} value={f.company_type} onChange={set("company_type")}>{COMPANY_TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
              <Field label="Years in Business"><input type="number" className={inputCls} value={f.years_in_business} onChange={set("years_in_business")} /></Field>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-5"><UsersThree size={18} weight="bold" /><h2 className="font-display font-bold text-lg text-[#0F172A]">Team</h2></div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              <Field label="Technicians"><input type="number" className={inputCls} value={f.num_technicians} onChange={set("num_technicians")} /></Field>
              <Field label="Programmers"><input type="number" className={inputCls} value={f.num_programmers} onChange={set("num_programmers")} /></Field>
              <Field label="Engineers"><input type="number" className={inputCls} value={f.num_engineers} onChange={set("num_engineers")} /></Field>
              <Field label="Project Managers"><input type="number" className={inputCls} value={f.num_project_managers} onChange={set("num_project_managers")} /></Field>
              <Field label="Salespeople"><input type="number" className={inputCls} value={f.num_salespeople} onChange={set("num_salespeople")} /></Field>
              <Field label="Total Employees"><input type="number" className={inputCls} value={f.num_employees} onChange={set("num_employees")} /></Field>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-5"><MapPin size={18} weight="bold" /><h2 className="font-display font-bold text-lg text-[#0F172A]">Geographic Market</h2></div>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Primary Service Area"><input data-testid="onb-primary-market" className={inputCls} value={f.primary_service_area} onChange={set("primary_service_area")} placeholder="New York, NY" /></Field>
              <Field label="City"><input data-testid="onb-city" className={inputCls} value={f.city} onChange={set("city")} placeholder="New York" /></Field>
              <Field label="State / Province"><input data-testid="onb-state" className={inputCls} value={f.state} onChange={set("state")} placeholder="NY" /></Field>
              <Field label="ZIP / Postal"><input className={inputCls} value={f.zip} onChange={set("zip")} /></Field>
              <Field label="Country"><input className={inputCls} value={f.country} onChange={set("country")} /></Field>
              <Field label="Additional Markets (comma-separated)"><input data-testid="onb-additional-markets" className={inputCls} value={f.additional_markets} onChange={set("additional_markets")} placeholder="Hamptons, NY, Palm Beach, FL" /></Field>
            </div>
          </div>

          <div className="flex justify-end pb-10">
            <button data-testid="onb-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 h-11 px-6 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors disabled:opacity-60">
              {saving ? "Saving…" : "Finish setup"} <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
